// Enlarged "main" avatar bust, the menu/customization counterpart to the small
// ChibiAvatar paper-doll. Composites the large waist-up character art (DATA/
// avatar/wu) by stacking each selected part's PNG, back→front by the manifest's
// per-slot `z`, at its `rect` offset on the 768×768 logical canvas (then scaled
// to displayHeight). Parts are addressed by the SAME {mnemonic,variant,color}
// the chibi uses, so one selection drives both this bust and the in-world chibi.
//
// Every part resolves from one canonical left-facing composition. The complete
// paper doll is mirrored as a unit when a right-facing presentation is needed.

import { Container, Sprite, Texture, Assets } from "pixi.js";
import type { PortraitManifest, PortraitSlot } from "@ygoevo/yo2-data";
import { asset } from "../data.ts";

/** Per-slot variant + colour the player has chosen. variant "00"/absent = skip. */
export type PortraitSelection = Record<string, { variant: string; color: number }>;

export type PortraitGender = "cm" | "cw";
export interface PortraitOptions {
  manifest: PortraitManifest;
  gender: PortraitGender;
  selection: PortraitSelection;
  /** On-screen height in px for the 768-tall canvas. */
  displayHeight: number;
  /** Mirror the canonical left-facing composite to face screen-right. */
  mirrored?: boolean;
  /** Idle eye-blink animation (duel busts). Default off (HUD chips/menus stay static). */
  blink?: boolean;
}

const CANVAS = 768;
// Idle-blink timing: pause between blinks, then step the eye strip open→closed→open.
const BLINK_MIN_MS = 2500;
const BLINK_VARY_MS = 4000;
const BLINK_FRAME_MS = 22;

export class PortraitAvatar {
  readonly root = new Container();
  private body = new Container();
  private manifest: PortraitManifest;
  private gender: PortraitGender;
  private selection: PortraitSelection;
  private mirrored: boolean;
  /** Bumped per rebuild so a slow texture load can't clobber a newer look. */
  private rebuildGen = 0;
  // Idle blink: the live eye sprite + its [open, …closed] frames, and the pending timer.
  private blinkEnabled: boolean;
  private eySprite: Sprite | null = null;
  private blinkFrames: Texture[] = [];
  private esSprite: Sprite | null = null;
  private detailBlinkFrames: Texture[] = [];
  private blinkTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(opts: PortraitOptions) {
    this.manifest = opts.manifest;
    this.gender = opts.gender;
    this.selection = opts.selection;
    this.mirrored = opts.mirrored ?? false;
    this.blinkEnabled = opts.blink ?? false;
    this.root.addChild(this.body);
    this.root.scale.set(opts.displayHeight / CANVAS);
    this.applyMirror();
  }

  get displayWidth(): number {
    return this.root.scale.x * CANVAS;
  }

  private slots(): PortraitSlot[] {
    return this.manifest.genders[this.gender]?.slots ?? [];
  }

  private worn(slot: string): boolean {
    const s = this.selection[slot];
    return !!s && s.variant !== "00";
  }

  /** Resolve a slot's layer (cel path + draw offset) for the current selection.
   *  A required slot with no explicit choice falls back to its first variant so
   *  the bust is always complete (e.g. rendering another player, or an old save
   *  that predates the portrait-only face slots). Colour degrades safely. */
  private resolve(slot: PortraitSlot): { path: string; ox: number; oy: number } | null {
    // `dc` Outfit and `ac` Costume are mutually exclusive — a costume REPLACES the
    // outfit. The customizer clears one when you equip the other, but old/foreign
    // saves can carry both; without this the bust stacks them (kaiser over blue).
    if (slot.slot === "dc" && this.worn("ac")) return null;
    // Supporting layers aren't chosen on their own. Back/style hair follow h1;
    // eye detail follows the eye shape and uses the body's skin colour. Deriving
    // here also heals legacy/foreign looks whose stored supporting slots disagree.
    const sel =
      slot.slot === "h2" || slot.slot === "hs"
        ? this.selection.h1
        : slot.slot === "es"
          ? this.selection.ey && {
              variant: this.selection.ey.variant,
              color: this.selection.bo?.color ?? 1,
            }
          : this.selection[slot.slot];
    const variant = sel?.variant ?? (slot.optional ? "00" : slot.variants[0]?.variant);
    if (!variant || variant === "00") return null;
    const v = slot.variants.find((x) => x.variant === variant);
    if (!v) return null;
    const byColor = v.cels[String(sel?.color ?? 1)] ?? v.cels[String(v.colors[0])];
    if (!byColor) return null;
    const path = byColor.r ?? byColor.n;
    return path ? { path, ox: v.rect[0], oy: v.rect[1] } : null;
  }

  private applyMirror(): void {
    this.body.scale.x = this.mirrored ? -1 : 1;
    this.body.x = this.mirrored ? CANVAS : 0;
  }

  async build(): Promise<void> {
    await this.rebuild();
  }

  /** Re-render for the current selection/gender/facing. Old layers stay on screen
   *  until the new textures have loaded, then swap in one frame, no blank flash
   *  (same approach as ChibiAvatar). */
  private async rebuild(): Promise<void> {
    const gen = ++this.rebuildGen;
    this.stopBlink(); // a fresh look replaces the eye sprite; drop the pending timer
    const sprites: Sprite[] = [];
    const loads: Promise<void>[] = [];
    let eySprite: Sprite | null = null;
    let eyPath: string | null = null;
    let esSprite: Sprite | null = null;
    let esPath: string | null = null;
    for (const slot of this.slots()) {
      const layer = this.resolve(slot);
      if (!layer) continue;
      const sprite = new Sprite();
      sprite.position.set(layer.ox, layer.oy);
      sprites.push(sprite);
      if (slot.slot === "ey") {
        eySprite = sprite;
        eyPath = layer.path;
      } else if (slot.slot === "es") {
        esSprite = sprite;
        esPath = layer.path;
      }
      loads.push(
        Assets.load(asset(layer.path))
          .then((tex: Texture) => {
            sprite.texture = tex;
            sprite.visible = true;
          })
          .catch(() => {
            sprite.visible = false;
          }),
      );
    }
    await Promise.all(loads);
    if (gen !== this.rebuildGen) {
      for (const s of sprites) s.destroy();
      return;
    }
    const old = this.body.removeChildren();
    for (const s of sprites) this.body.addChild(s);
    for (const c of old) c.destroy();

    this.eySprite = eySprite;
    this.esSprite = esSprite;
    this.blinkFrames = [];
    this.detailBlinkFrames = [];
    if (this.blinkEnabled && eySprite && eyPath) {
      const [frames, detailFrames] = await Promise.all([
        this.loadBlinkFrames(eyPath, eySprite.texture),
        esSprite && esPath ? this.loadBlinkFrames(esPath, esSprite.texture) : Promise.resolve([]),
      ]);
      if (gen !== this.rebuildGen) return; // a newer look took over while loading
      this.blinkFrames = frames;
      this.detailBlinkFrames = detailFrames;
      this.scheduleBlink();
    }
  }

  /** The manifest pins each eye layer to its open frame; the closed frames live beside it
   *  under the SAME name with the openness field bumped (…_{ey|es}_VV_OO_cCC.png). We
   *  walk OO upward from the open frame until one 404s — that's the blink ladder.
   *  ponytail: derived from the path, not the manifest (which carries no blink
   *  track). Add a `cels.blink` array to the decoder if you want it data-driven. */
  private async loadBlinkFrames(openPath: string, openTex: Texture): Promise<Texture[]> {
    const m = openPath.match(/_(?:ey|es)_\d+_(\d+)_c\d+\.png$/);
    const restStr = m?.[1];
    if (!restStr) return [];
    const rest = parseInt(restStr, 10);
    const pad = restStr.length;
    const frames: Texture[] = [openTex];
    for (let o = rest + 1; o <= rest + 8; o++) {
      const oo = String(o).padStart(pad, "0");
      const path = openPath.replace(/(_(?:ey|es)_\d+_)\d+(_c\d+\.png)$/, `$1${oo}$2`);
      try {
        frames.push(await Assets.load(asset(path)));
      } catch {
        break; // frames are contiguous; first miss = end of the ladder
      }
    }
    return frames;
  }

  private scheduleBlink(): void {
    if (!this.blinkEnabled || this.blinkFrames.length < 2) return;
    this.blinkTimer = setTimeout(() => this.playBlink(), BLINK_MIN_MS + Math.random() * BLINK_VARY_MS);
  }

  /** Step open→closed→open through the ladder, then schedule the next blink. */
  private playBlink(): void {
    const last = this.blinkFrames.length - 1;
    const seq: number[] = [];
    for (let i = 1; i <= last; i++) seq.push(i); // closing
    for (let i = last - 1; i >= 0; i--) seq.push(i); // opening back to rest
    let k = 0;
    const step = () => {
      const idx = seq[k++];
      if (idx === undefined) {
        this.scheduleBlink();
        return;
      }
      const tex = this.blinkFrames[idx];
      if (!this.eySprite || this.eySprite.destroyed || !tex) {
        this.scheduleBlink();
        return;
      }
      this.eySprite.texture = tex;
      const detailTex = this.detailBlinkFrames[idx];
      if (this.esSprite && !this.esSprite.destroyed && detailTex) this.esSprite.texture = detailTex;
      this.blinkTimer = setTimeout(step, BLINK_FRAME_MS);
    };
    step();
  }

  private stopBlink(): void {
    if (this.blinkTimer) {
      clearTimeout(this.blinkTimer);
      this.blinkTimer = null;
    }
  }

  /** Apply a new look. Mirroring is a transform and never reloads another asset. */
  set(next: { selection?: PortraitSelection; gender?: PortraitGender; mirrored?: boolean }): void {
    const rebuild = Boolean(next.selection || next.gender);
    if (next.selection) this.selection = next.selection;
    if (next.gender) this.gender = next.gender;
    if (next.mirrored !== undefined && next.mirrored !== this.mirrored) {
      this.mirrored = next.mirrored;
      this.applyMirror();
    }
    if (rebuild) void this.rebuild();
  }

  destroy(): void {
    this.stopBlink();
    this.root.destroy({ children: true });
  }
}
